<template>
  <card-component
    truncate
    class="resource file"
    size="xsmall"
    :class="[{ moe: checkIfMoe, community: checkIfCommunity }]"
    @on-click="handlePrimaryCardAction"
  >
    <template #label>
      <span class="location">{{ location(resourceData.resourceDomain) }}</span>
    </template>
    <template #thumbnail>
      <span class="icon">
        <svg-icon :name="iconType('MEDIA', fileType)" kind="indicative" />
      </span>
    </template>
    <template #header>
      <h6 class="title">{{ resourceData.title }}</h6>
    </template>
    <template #metadata>
      <li>{{ fileTypeLabel }}</li>
    </template>
    <template #actions>
      <cv-button v-if="showMenu" class="icon-button" kind="ghost" @click="handleDownload">
        <svg-icon name="Download24" kind="action" />
      </cv-button>
    </template>
  </card-component>
</template>

<script setup lang="ts">
import mediaObjectApi from "@apis/media.object.api";
import SearchResourceDetailContentSubpage from "@modules/search/components/SearchResourceDetailContentSubpage";
import SearchSelectionMixin from "@modules/search/mixin/search.selection.mixin";
import LessonCardMixin from "@plugins/common/mixin/lesson.card.mixin";
import ResourceMixin from "@plugins/common/mixin/resource.mixin";
import StringUtils from "@utils/string.utils";
import { MEDIA_TYPE } from "@modules/search/service/search.constant";
import { ref, computed, onMounted } from "vue";
import { useVm } from "@utils/vm.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useRouting } from "@plugins/common/composables/routing.ts";

// Mixin
defineOptions({
  mixins: [SearchSelectionMixin, LessonCardMixin, ResourceMixin],
});

// Props
interface Props {
  resourceData?: Record<string, unknown>;
  fileType?: string | null;
  size?: string;
  searchFilters?: Record<string, unknown>;
}

const props = withDefaults(defineProps<Props>(), {
  resourceData: null,
  fileType: null,
  size: "xsmall",
  searchFilters: () => ({}),
});

// Emits
const emit = defineEmits<{
  actionBack: [];
  selectionSaved: [];
}>();

const { vm } = useVm();
const contentSubpageInstance = useContentSubpageInstance();
const { passiveApiFailHandler } = useErrorHandler();
const { target, urlReady, handleRouteIntervalCheck } = useRouting();

// Data
const mediaObject = ref({});
const isLoading = ref(true);

// Computed
const actionTriggeredOnCardClick = computed(() => {
  // Based on card patterns, the action that should trigger upon clicking the card
  // is the card's last available action.
  // Hence checking of which action is allowed must be done in sequence.
  if (vm.isSingleSelection) return handleChooseSelection;
  return handleViewDetails;
});

const showMenu = computed(() => {
  return props.resourceData.downloadable;
});

const fileTypeLabel = computed(() => {
  if (
    props.fileType == MEDIA_TYPE.PDF ||
    props.fileType == MEDIA_TYPE.ZIP ||
    props.fileType == MEDIA_TYPE.HTML5
  ) {
    return props.fileType;
  }
  return StringUtils.toTitleCase(props.fileType);
});

// Methods
const handleViewDetails = () => {
  contentSubpageInstance.show(SearchResourceDetailContentSubpage, {
    baseTitle: vm.subpageTitle,
    hasParentSubpage: vm.isSelection,
    uuid: props.resourceData.uuid,
    title: props.resourceData.title,
    type: "MEDIA",
    location: vm.location(props.resourceData.resourceDomain),
    addFromLocation: vm.searchContext.addFromLocation,
    searchFilters: props.searchFilters,
    actionBack: () => {
      emit("actionBack");
    },
  });
};

const handlePrimaryCardAction = () => {
  const action = actionTriggeredOnCardClick.value;
  action();
};

const handleDownload = () => {
  const uuid = mediaObject.value.uuid;
  handleRouteIntervalCheck();
  mediaObjectApi.downloadMediaObject({ uuid: uuid }).then((response) => {
    //OK
    if (response.code === "ok") {
      target.value = "DOWNLOAD_MEDIA_OBJECT";
      urlReady.value = response.data;
    } else {
      //todo: error message
    }
  }, passiveApiFailHandler); //OK
};

const handleChooseSelection = () => {
  vm.addSelection();
  emit("selectionSaved");
};

// Lifecycle hooks
onMounted(() => {
  mediaObjectApi.getMetaData({ uuid: props.resourceData.uuid }).then((response) => {
    //OK
    if (response) {
      isLoading.value = false;
      mediaObject.value = response.data;
      if (!mediaObject.value.accessible) {
        //todo: render non available mediaObject
      }
    }
  }, passiveApiFailHandler); //OK
});
</script>
